import React from 'react';
import PropTypes from 'prop-types';
import { UploadedPhoto } from '../../prop-types';
import Filters from '../../mobile/advanced-editor/filters';
import EditButtons from './edit-buttons';
import DisplayEditValue from './display-edit-value';
import SliderControl from '../../mobile/advanced-editor/slider-control';
import Navigation from '../../common/advanced-editor/navigation';
import { MODES } from '../../common/advanced-editor/constants';
import IU from '../../../_utils/img/img-utils';

const { zoomHelpers } = IU;

export function SliderAndButtonsPanel({
  close,
  editMode,
  photo,
  effectUrls,
  initialZoom,
  handleApply,
  handleReset,
  handleEditMode,
  handleZoom,
  handleStraighten,
  handleBrightness,
  handleEffect,
  ...refs
}) {
  const { CROP, ROTATE, STRAIGHTEN, BRIGHTNESS } = MODES;
  const imageId = (photo || {}).image_id;

  const filtersEnabled = editMode === MODES.FILTERS;
  const filtersCss = filtersEnabled ? 'd-flex' : 'd-none';
  const controlsCss = filtersEnabled ? 'd-none' : 'd-flex';

  const sliderAttributes = {
    [CROP]: {
      ref: refs.zoomSliderRef,
      onChange: handleZoom,
      value:
        zoomHelpers.getZoom(imageId) ||
        zoomHelpers.getZoomFromPhotoData(photo) ||
        initialZoom,
      min: initialZoom,
      max: 2.5,
      threshold: 100,
    },
    [BRIGHTNESS]: {
      ref: refs.brightnessSliderRef,
      onChange: handleBrightness,
      value: zoomHelpers.getBrightness(imageId),
      min: 5,
      max: 15,
      threshold: 200,
    },
    [STRAIGHTEN]: {
      ref: refs.tiltSliderRef,
      onChange: handleStraighten,
      value: zoomHelpers.getStraighten(imageId),
      min: -20,
      max: 20,
      threshold: 200,
    },
    [ROTATE]: {
      value: photo?.angle || 0,
      onChange: () => {}, // Keep slider displayed for placement purposes
    },
  };

  const currentSlider = Object.assign({ editMode }, sliderAttributes[editMode]);
  const photoEditsCurrentValues = Object.entries(sliderAttributes).reduce(
    (values, [key, { value }]) => {
      return { ...values, ...{ [key]: DisplayEditValue.toFixedString(value) } };
    },
    {}
  );

  return (
    <div className="advanced-editor__slider-and-buttons-panel w-100">
      {photo && (
        <Filters
          className={filtersCss}
          handleEditMode={handleEditMode}
          photo={photo}
          effectUrls={effectUrls}
          handleEffect={handleEffect}
        />
      )}
      <EditButtons
        className={`${controlsCss} w-50`}
        editMode={editMode}
        handleEditMode={handleEditMode}
        photoEditsCurrentValues={photoEditsCurrentValues}>
        <DisplayEditValue
          ref={refs.displayEditValueRef}
          hideValue={editMode === ROTATE}
          value={currentSlider.value}
        />
      </EditButtons>
      <SliderControl
        ref={currentSlider.ref}
        disabled={editMode === ROTATE}
        {...currentSlider}
      />
      <Navigation
        handleApply={handleApply}
        close={close}
        handleReset={handleReset}
      />
    </div>
  );
}

SliderAndButtonsPanel.propTypes = {
  photo: UploadedPhoto,
  editMode: PropTypes.string.isRequired,
  initialZoom: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  effectUrls: PropTypes.shape({
    [PropTypes.string]: PropTypes.string,
  }),
  refs: PropTypes.shape({
    [PropTypes.string]: PropTypes.func,
  }),
  handleZoom: PropTypes.func.isRequired,
  handleStraighten: PropTypes.func.isRequired,
  handleBrightness: PropTypes.func.isRequired,
  handleRotate: PropTypes.func.isRequired,
  handleEffect: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
  handleApply: PropTypes.func.isRequired,
};

SliderAndButtonsPanel.defaultProps = {
  photo: null,
  refs: {},
  effectUrls: {},
};
